import { db } from './firebase';
import { doc, collection, query, where, getDocs, getDoc, setDoc, updateDoc, deleteDoc, orderBy } from 'firebase/firestore';

// Collection names
export const COLLECTIONS = {
  PARTNERS: 'partners',
  PRODUCTS: 'products',
  ORDERS: 'orders',
  INVENTORY: 'inventory',
  GEOSPATIAL_DEMAND: 'geospatialDemand',
  USERS: 'users'
};

// Partner schema helper functions
export const partnerAPI = {
  // Create a new partner
  async createPartner(partnerId, partnerData) {
    const partnerRef = doc(db, COLLECTIONS.PARTNERS, partnerId);
    await setDoc(partnerRef, {
      ...partnerData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      verificationStatus: 'pending'
    });
  },

  // Get partner by ID
  async getPartner(partnerId) {
    const partnerRef = doc(db, COLLECTIONS.PARTNERS, partnerId);
    const partnerDoc = await getDoc(partnerRef);
    return partnerDoc.exists() ? { id: partnerDoc.id, ...partnerDoc.data() } : null;
  },

  // Update partner
  async updatePartner(partnerId, updates) {
    const partnerRef = doc(db, COLLECTIONS.PARTNERS, partnerId);
    await updateDoc(partnerRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
  }
};

// Product schema helper functions
export const productAPI = {
  // Create a new product
  async createProduct(productData) {
    const productsRef = collection(db, COLLECTIONS.PRODUCTS);
    const newProductRef = doc(productsRef);
    await setDoc(newProductRef, {
      ...productData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return newProductRef.id;
  },

  // Get products by partner
  async getPartnerProducts(partnerId) {
    const q = query(
      collection(db, COLLECTIONS.PRODUCTS),
      where('partnerId', '==', partnerId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }
};

// Inventory schema helper functions
export const inventoryAPI = {
  // Update inventory
  async updateInventory(inventoryId, updates) {
    const inventoryRef = doc(db, COLLECTIONS.INVENTORY, inventoryId);
    await updateDoc(inventoryRef, {
      ...updates,
      updatedAt: new Date().toISOString()
    });
  },

  // Get inventory by product
  async getProductInventory(productId) {
    const q = query(
      collection(db, COLLECTIONS.INVENTORY),
      where('productId', '==', productId)
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }
};

// Order schema helper functions
export const orderAPI = {
  // Create new order
  async createOrder(orderData) {
    const ordersRef = collection(db, COLLECTIONS.ORDERS);
    const newOrderRef = doc(ordersRef);
    await setDoc(newOrderRef, {
      ...orderData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      status: 'pending'
    });
    return newOrderRef.id;
  },

  // Get partner orders
  async getPartnerOrders(partnerId) {
    const q = query(
      collection(db, COLLECTIONS.ORDERS),
      where('partnerId', '==', partnerId),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }
};

// Geospatial demand schema helper functions
export const geospatialAPI = {
  // Add demand data
  async addDemandData(data) {
    const demandsRef = collection(db, COLLECTIONS.GEOSPATIAL_DEMAND);
    const newDemandRef = doc(demandsRef);
    await setDoc(newDemandRef, {
      ...data,
      timestamp: new Date().toISOString()
    });
    return newDemandRef.id;
  },

  // Get regional demand
  async getRegionalDemand(regionId) {
    const q = query(
      collection(db, COLLECTIONS.GEOSPATIAL_DEMAND),
      where('regionId', '==', regionId),
      orderBy('timestamp', 'desc')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  }
};

// User schema helper functions
export const userAPI = {
  async createUserProfile(uid, userData) {
    const userRef = doc(db, COLLECTIONS.USERS, uid);
    await setDoc(userRef, {
      uid,
      name: userData.name || '',
      email: userData.email || '',
      phone: userData.phone || null,
      userType: userData.userType || 'user',
      profileImageUrl: userData.profileImageUrl || null,
      address: userData.address || null,
      status: userData.status || 'active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Add any other default fields for a user profile
    }, { merge: true }); // Use merge to avoid overwriting if doc exists (e.g., from social login)
  },

  async getUserProfile(uid) {
    const userRef = doc(db, COLLECTIONS.USERS, uid);
    const userDoc = await getDoc(userRef);
    return userDoc.exists() ? { id: userDoc.id, ...userDoc.data() } : null;
  },

  async updateUserProfile(uid, updates) {
    const userRef = doc(db, COLLECTIONS.USERS, uid);
    await updateDoc(userRef, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
  },
};